import React, { useState } from "react";
import { NavLink, useLocation } from "react-router-dom";

const NAVY  = "#1e3a8a";
const BLUE  = "#2563eb";
const SKY   = "#0ea5e9";
const LIGHT = "#f0f7ff";

const ROLES = [
  { path: "/docs",                  label: "Overview",           exact: true },
  { path: "/docs/getting-started",  label: "Getting Started" },
  { path: "/docs/ai-assistant",     label: "AI Chat Assistant" },
  { path: "/docs/recruitment",      label: "Recruitment" },
  { path: "/docs/people-hr",        label: "People & HR" },
  { path: "/docs/finance",          label: "Finance" },
  { path: "/docs/admin-settings",   label: "Admin & Settings" },
];

const ROLE_COLORS: Record<string, string> = {
  "/docs/getting-started": "#0ea5e9",
  "/docs/ai-assistant":    "#ec4899",
  "/docs/recruitment":     "#7c3aed",
  "/docs/people-hr":       "#059669",
  "/docs/finance":         "#d97706",
  "/docs/admin-settings":  "#1e3a8a",
  "/docs":                 "#64748b",
};

const ROLE_TAGS: Record<string, string> = {
  admin: "#1e3a8a", admin_assistant: "#059669", hr: "#7c3aed", recruiter: "#0891b2",
  finance: "#d97706", employee: "#0ea5e9",
};
const ROLE_TAG_LABELS: Record<string, string> = {
  admin: "Admin", admin_assistant: "Admin Assistant", hr: "HR", recruiter: "Recruiter",
  finance: "Finance", employee: "Employee",
};

export function AvailableTo({ roles }: { roles: (keyof typeof ROLE_TAGS)[] }) {
  return (
    <div style={{ display: "flex", flexWrap: "wrap", gap: 6, alignItems: "center", marginBottom: 4 }}>
      <span style={{ fontSize: 11, fontWeight: 700, color: "#94a3b8", textTransform: "uppercase", letterSpacing: "0.06em", marginRight: 2 }}>Available to</span>
      {roles.map(r => (
        <span key={r} style={{
          fontSize: 11, fontWeight: 600, padding: "2px 8px", borderRadius: 20,
          background: ROLE_TAGS[r] + "15", color: ROLE_TAGS[r], border: `1px solid ${ROLE_TAGS[r]}30`,
        }}>{ROLE_TAG_LABELS[r]}</span>
      ))}
    </div>
  );
}

export function ScreenshotBox({ label }: { label: string }) {
  return (
    <div style={{
      border: "2px dashed #cbd5e1", borderRadius: 10, background: "#f8fafc",
      padding: "48px 24px", textAlign: "center", margin: "20px 0", color: "#94a3b8",
    }}>
      <div style={{ fontSize: 28, marginBottom: 8 }}>📸</div>
      <div style={{ fontSize: 13, fontWeight: 600 }}>{label}</div>
      <div style={{ fontSize: 11, marginTop: 4 }}>Screenshot placeholder</div>
    </div>
  );
}

export function FeatureBadge({ label, color = "#0ea5e9" }: { label: string; color?: string }) {
  return (
    <span style={{
      display: "inline-block", background: color + "15", color,
      border: `1px solid ${color}30`, borderRadius: 6,
      padding: "3px 10px", fontSize: 12, fontWeight: 600, marginRight: 6, marginBottom: 6,
    }}>{label}</span>
  );
}

export function Section({ title, children, id }: React.PropsWithChildren<{ title: string; id?: string }>) {
  return (
    <div id={id} style={{ marginBottom: 48, scrollMarginTop: 72 }}>
      <h2 style={{ fontSize: 20, fontWeight: 700, color: "#0f172a", borderBottom: "2px solid #e2e8f0", paddingBottom: 10, marginBottom: 20 }}>{title}</h2>
      {children}
    </div>
  );
}

export function JumpNav({ items, color }: { items: { id: string; label: string }[]; color: string }) {
  return (
    <div style={{
      display: "flex", flexWrap: "wrap", gap: 8, background: "#fff",
      border: "1px solid #e2e8f0", borderRadius: 10, padding: "14px 16px", marginBottom: 32,
    }}>
      {items.map(it => (
        <a key={it.id} href={`#${it.id}`} style={{
          fontSize: 12.5, fontWeight: 600, color, textDecoration: "none",
          background: color + "10", border: `1px solid ${color}25`,
          borderRadius: 20, padding: "4px 12px",
        }}>{it.label}</a>
      ))}
    </div>
  );
}

export function Steps({ items }: { items: string[] }) {
  return (
    <ol style={{ paddingLeft: 0, margin: "16px 0", listStyle: "none" }}>
      {items.map((item, i) => (
        <li key={i} style={{ display: "flex", gap: 14, marginBottom: 12, alignItems: "flex-start" }}>
          <span style={{ flexShrink: 0, width: 26, height: 26, borderRadius: "50%", background: NAVY, color: "white", display: "flex", alignItems: "center", justifyContent: "center", fontSize: 12, fontWeight: 700 }}>{i + 1}</span>
          <span style={{ fontSize: 14, color: "#334155", lineHeight: 1.6, paddingTop: 3 }}>{item}</span>
        </li>
      ))}
    </ol>
  );
}

export function Callout({ icon, title, body }: { icon: string; title: string; body: string }) {
  return (
    <div style={{ background: "#eff6ff", border: "1px solid #bfdbfe", borderRadius: 8, padding: "14px 18px", margin: "16px 0", display: "flex", gap: 12, alignItems: "flex-start" }}>
      <span style={{ fontSize: 18, flexShrink: 0 }}>{icon}</span>
      <div>
        <div style={{ fontSize: 13, fontWeight: 700, color: "#1e40af", marginBottom: 2 }}>{title}</div>
        <div style={{ fontSize: 13, color: "#374151", lineHeight: 1.5 }}>{body}</div>
      </div>
    </div>
  );
}

export default function DocsLayout({ children }: React.PropsWithChildren<{}>) {
  const location = useLocation();
  const [menuOpen, setMenuOpen] = useState(false);

  return (
    <div style={{ minHeight: "100vh", fontFamily: "var(--font-sans)", background: "#f8fafc" }}>
      <style>{`
        .docs-nav-link { color: #475569; text-decoration: none; font-size: 14px; font-weight: 500; padding: 8px 14px; border-radius: 8px; transition: all 0.15s; }
        .docs-nav-link:hover { color: ${NAVY}; background: ${LIGHT}; }
        .docs-nav-link.active { color: ${NAVY}; font-weight: 700; }
        .docs-cta-primary { background: ${NAVY}; color: #fff; text-decoration: none; font-weight: 700; font-size: 14px; padding: 9px 20px; border-radius: 8px; display: inline-block; }
        .docs-cta-secondary { background: transparent; color: ${NAVY}; text-decoration: none; font-weight: 600; font-size: 14px; padding: 8px 18px; border-radius: 8px; border: 1.5px solid ${NAVY}; display: inline-block; }
        @media (max-width: 768px) {
          .docs-desktop-nav   { display: none !important; }
          .docs-desktop-ctas  { display: none !important; }
          .docs-hamburger     { display: flex !important; }
          .docs-sidebar       { display: none !important; }
          .docs-main          { margin-left: 0 !important; padding: 16px !important; }
          .docs-guide-tabs    { display: flex !important; }
        }
        @media (min-width: 769px) {
          .docs-hamburger  { display: none !important; }
          .docs-guide-tabs { display: none !important; }
        }
      `}</style>

      {/* ── Navbar (same style as landing/pricing) ── */}
      <header style={{ position: "fixed", top: 0, left: 0, right: 0, zIndex: 200, background: "#fff", borderBottom: "1px solid #e2e8f0" }}>
        <div style={{ height: 56, display: "flex", alignItems: "center", padding: "0 24px", gap: 8 }}>
          {/* Logo */}
          <a href="/" style={{ display: "flex", alignItems: "center", gap: 8, textDecoration: "none" }}>
            <div style={{ width: 30, height: 30, borderRadius: 7, background: `linear-gradient(135deg, ${SKY}, ${NAVY})`, display: "flex", alignItems: "center", justifyContent: "center", color: "#fff", fontWeight: 800, fontSize: 14 }}>H</div>
            <span style={{ fontWeight: 800, fontSize: 16, color: NAVY }}>HRxAI</span>
          </a>
          <span style={{ background: "#f1f5f9", color: "#64748b", fontSize: 11, fontWeight: 600, padding: "2px 8px", borderRadius: 4 }}>Docs</span>

          {/* Desktop nav links */}
          <div className="docs-desktop-nav" style={{ display: "flex", alignItems: "center", gap: 2, marginLeft: 8 }}>
            <a href="/#features"     className="docs-nav-link">Features</a>
            <a href="/#how-it-works" className="docs-nav-link">How it works</a>
            <a href="/pricing"       className="docs-nav-link">Pricing</a>
            <a href="/docs"          className="docs-nav-link active">Docs</a>
          </div>

          <div style={{ flex: 1 }} />

          {/* Desktop CTAs */}
          <div className="docs-desktop-ctas" style={{ display: "flex", alignItems: "center", gap: 10 }}>
            <a href="/login"  className="docs-cta-secondary">Sign in</a>
            <a href="/signup" className="docs-cta-primary">Get started free →</a>
          </div>

          {/* Hamburger — mobile only */}
          <button
            className="docs-hamburger"
            onClick={() => setMenuOpen(m => !m)}
            style={{ display: "none", background: "none", border: "none", cursor: "pointer", padding: 8, flexDirection: "column", gap: 5 }}
          >
            <span style={{ display: "block", width: 22, height: 2, background: NAVY, borderRadius: 2, transition: "all 0.2s", transform: menuOpen ? "rotate(45deg) translate(5px, 5px)" : "none" }} />
            <span style={{ display: "block", width: 22, height: 2, background: NAVY, borderRadius: 2, opacity: menuOpen ? 0 : 1, transition: "all 0.2s" }} />
            <span style={{ display: "block", width: 22, height: 2, background: NAVY, borderRadius: 2, transition: "all 0.2s", transform: menuOpen ? "rotate(-45deg) translate(5px, -5px)" : "none" }} />
          </button>
        </div>

        {/* Mobile guide tabs — scrollable pills */}
        <div className="docs-guide-tabs" style={{
          display: "none", borderTop: "1px solid #e2e8f0",
          padding: "8px 12px", gap: 8, overflowX: "auto", background: "#fff",
        }}>
          {ROLES.map(r => {
            const isActive = r.exact ? location.pathname === r.path : location.pathname.startsWith(r.path);
            const color = ROLE_COLORS[r.path] || NAVY;
            return (
              <a key={r.path} href={r.path} style={{
                flexShrink: 0, padding: "5px 14px", borderRadius: 20, fontSize: 13,
                fontWeight: isActive ? 700 : 500, textDecoration: "none", whiteSpace: "nowrap",
                background: isActive ? color + "15" : "#f1f5f9",
                color: isActive ? color : "#64748b",
                border: isActive ? `1.5px solid ${color}40` : "1.5px solid transparent",
              }}>{r.label}</a>
            );
          })}
        </div>
      </header>

      {/* Mobile hamburger dropdown */}
      {menuOpen && (
        <div style={{
          position: "fixed", top: 56, left: 0, right: 0, zIndex: 199,
          background: "#fff", borderBottom: "1px solid #e2e8f0",
          padding: "12px 16px", display: "flex", flexDirection: "column", gap: 4,
          boxShadow: "0 8px 24px rgba(0,0,0,0.10)",
        }}>
          {[
            { href: "/", label: "Home" },
            { href: "/#features", label: "Features" },
            { href: "/pricing", label: "Pricing" },
            { href: "/docs", label: "Docs" },
          ].map(l => (
            <a key={l.href} href={l.href} onClick={() => setMenuOpen(false)}
              style={{ padding: "10px 14px", borderRadius: 8, fontSize: 15, fontWeight: 500, color: "#0f172a", textDecoration: "none" }}>
              {l.label}
            </a>
          ))}
          <div style={{ borderTop: "1px solid #f1f5f9", marginTop: 8, paddingTop: 12, display: "flex", flexDirection: "column", gap: 8 }}>
            <a href="/login"  className="docs-cta-secondary" style={{ textAlign: "center" }}>Sign in</a>
            <a href="/signup" className="docs-cta-primary"   style={{ textAlign: "center" }}>Get started free →</a>
          </div>
        </div>
      )}

      {/* Body */}
      <div style={{ display: "flex", paddingTop: 56 }}>
        {/* Sidebar — desktop only */}
        <nav className="docs-sidebar" style={{
          width: 220, flexShrink: 0, position: "fixed", top: 56, bottom: 0,
          background: "#fff", borderRight: "1px solid #e2e8f0", overflowY: "auto", padding: "24px 0",
        }}>
          <div style={{ padding: "0 16px 12px", fontSize: 11, fontWeight: 700, color: "#94a3b8", letterSpacing: "0.08em", textTransform: "uppercase" }}>Categories</div>
          {ROLES.map(r => (
            <NavLink key={r.path} to={r.path} end={r.exact}
              style={({ isActive }) => ({
                display: "flex", alignItems: "center", gap: 8, padding: "9px 16px",
                textDecoration: "none", fontSize: 14, fontWeight: isActive ? 700 : 500,
                color: isActive ? (ROLE_COLORS[r.path] || NAVY) : "#475569",
                background: isActive ? (ROLE_COLORS[r.path] || NAVY) + "10" : "transparent",
                borderRight: isActive ? `3px solid ${ROLE_COLORS[r.path] || NAVY}` : "3px solid transparent",
                transition: "all 0.15s",
              })}
            >{r.label}</NavLink>
          ))}
          <div style={{ padding: "24px 16px 8px", fontSize: 11, fontWeight: 700, color: "#94a3b8", letterSpacing: "0.08em", textTransform: "uppercase" }}>Resources</div>
          <a href="https://www.hrxai.app" target="_blank" rel="noreferrer"
            style={{ display: "block", padding: "9px 16px", textDecoration: "none", fontSize: 14, fontWeight: 500, color: "#475569" }}>
            Open HRxAI →
          </a>
        </nav>

        {/* Main content */}
        <main className="docs-main" style={{ marginLeft: 220, flex: 1, padding: "40px 48px", maxWidth: 860, overflowX: "hidden" }}>
          {children}
        </main>
      </div>
    </div>
  );
}
